/**
 * GifSearch — quality-measured compression search for GIFs.
 *
 * WHAT WAS WRONG (bug #1422, "из GIF делает пиксельное месево, ещё и с плохо
 * регулируемым размером", and the seven size-complaints before it)
 * ---------------------------------------------------------------------------
 * The old search was a single scalar "aggression" ladder: A ∈ [0,1] mapped to
 * `--lossy=200` + a palette walk 256→24, then resolution, then frame drop. It
 * optimised bytes and had no way to observe quality, so it always paid in the
 * currency its ladder happened to list first. Measured on real GIFs with
 * S-CIELAB ΔE (see gif-quality-metric.js) and VMAF:
 *
 *   photographic foliage @ ratio 0.39   ladder ΔE 6.00 / VMAF 81.7
 *                                        best available ΔE 4.49 / VMAF 95.4
 *   office video @ ratio 0.22           ladder ΔE 4.48 / VMAF 80.2
 *                                        best available ΔE 1.83 / VMAF 82.0
 *   flat art @ ratio 0.44               ladder ΔE 0.19  (here it is fine)
 *
 * and — the reason no fixed ladder can be right — the winning knob FLIPS with
 * content: `--lossy` wins on smooth video, a fresh ffmpeg palette wins on dense
 * photographic detail, plain `--colors` wins on flat art, and downscaling wins
 * on photographic content at aggressive ratios while being the worst possible
 * choice for anything with text.
 *
 * HOW THIS WORKS
 * --------------
 * 1. Build a PROXY: ~8 frames sampled out of the animation (`--delete` the
 *    rest — frame SELECTION with `#n` corrupts optimised GIFs, deletion
 *    composites correctly).
 * 2. Sweep every knob family on the proxy and score each result with
 *    GifQuality (S-CIELAB ΔE). Measured Spearman ρ between proxy ranking and
 *    full-file ranking: 0.96 / 0.99 / 0.98 across the three content classes,
 *    at 6-16× less work per candidate.
 *    (Note the 2026-06-23 finding that a proxy CANNOT predict SIZE — 7-18%
 *    error — still holds and is respected: the proxy ranks quality only, sizes
 *    are always measured on the real file.)
 * 3. Pick the family that is best at the size the user actually asked for,
 *    then bisect that one knob on the FULL file until the byte target is hit.
 *
 * Net effect vs. the old search: fewer full-file gifsicle runs (3-5 instead of
 * 8-14) and the output is the best-looking one of the settings that fit.
 *
 * Engine access is injected so this module stays testable outside a browser:
 * the offline fixture battery (tests/playwright/gif-compressor-quality.mjs)
 * drives the SAME code as production.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) module.exports = api;
    if (root) root.GifSearch = api;
}(typeof self !== 'undefined' ? self : typeof globalThis !== 'undefined' ? globalThis : null, function () {
    'use strict';

    var PROXY_FRAMES = 8;      // ρ=0.96-0.99 vs full at this count; 4 was not enough
    var METRIC_FRAMES = 4;     // frames actually scored (of the proxy's 8)
    var METRIC_WIDTH = 320;    // working resolution for the metric
    // ΔE above this is "you can see it" — the trigger for spending 30 MB on the
    // FFmpeg palette lane, which only pays off on hard (photographic) content.
    var VISIBLE_DAMAGE = 2.5;

    function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

    /**
     * Knob families. Each is ONE monotone axis: bigger `t` (0..1) = smaller
     * output = more damage. `args(t, ctx)` returns gifsicle args, or a
     * {palette:K} marker for the FFmpeg lane.
     */
    var FAMILIES = {
        // gifsicle lossy-LZW. Best on smooth photographic video.
        lossy: {
            needsFfmpeg: false,
            arg: function (t, ctx) {
                var L = Math.round(5 + t * 195);
                return { gifsicle: ['-O' + ctx.opt, '--lossy=' + L], label: 'lossy=' + L };
            }
        },
        // gifsicle palette reduction. Best on flat art, where the source
        // barely uses its palette anyway.
        colors: {
            needsFfmpeg: false,
            arg: function (t, ctx) {
                // GEOMETRIC, not linear: output size tracks log(palette size),
                // so a linear walk spends most of its range between 256 and 70
                // colours (where almost nothing happens) and then falls off a
                // cliff into single digits. 256 → 8 geometrically puts the
                // probes where the size actually moves.
                var C = clamp(Math.round(256 * Math.pow(8 / 256, t)), 8, 256);
                return { gifsicle: C >= 256 ? ['-O' + ctx.opt] : ['-O' + ctx.opt, '--colors', String(C)], label: 'colors=' + C };
            }
        },
        // downscale. Best on photographic content at aggressive ratios; the
        // WORST option for anything with text — which is why it is a measured
        // candidate now instead of a fixed rung near the bottom of a ladder.
        scale: {
            needsFfmpeg: false,
            arg: function (t, ctx) {
                var s = 1 - t * 0.6;                  // 100% → 40%
                var rw = Math.max(64, Math.round(ctx.width * s));
                if (rw >= ctx.width) return { gifsicle: ['-O' + ctx.opt, '--lossy=30'], label: 'scale=1.00' };
                return { gifsicle: ['-O' + ctx.opt, '--lossy=30', '--resize-width', String(rw)], label: 'scale=' + s.toFixed(2) };
            }
        },
        // Everything at once. The one virtue of the ladder this replaced was
        // that its tail STACKED palette + resolution, so it could reach ratios
        // no single knob can: measured, a 9.7 MB screen recording asked down to
        // 10 % of its size is out of reach for lossy, colours, palette and
        // scale individually, and the search returned nothing at all until this
        // family existed. It ranks last on quality (correctly — it is the most
        // damaging option), so it is only ever chosen when nothing else fits.
        combo: {
            needsFfmpeg: false,
            arg: function (t, ctx) {
                var C = clamp(Math.round(64 * Math.pow(8 / 64, t)), 8, 64);
                var s = 1 - t * 0.65;
                var args = ['-O' + ctx.opt, '--lossy=120', '--colors', String(C)];
                var rw = Math.max(64, Math.round(ctx.width * s));
                if (rw < ctx.width) { args.push('--resize-width'); args.push(String(rw)); }
                return { gifsicle: args, label: 'combo colors=' + C + ' scale=' + s.toFixed(2) };
            }
        },
        // fresh palette computed from the true colours + ordered dithering.
        // Dominates everything on dense photographic content (foliage, grain).
        palette: {
            needsFfmpeg: true,
            arg: function (t, ctx) {
                var K = clamp(Math.round(255 * Math.pow(12 / 255, t)), 12, 255);   // geometric, see `colors`
                return { palette: K, post: ['-O' + ctx.opt], label: 'palette=' + K };
            }
        }
    };

    /**
     * @param {object} io
     *   runGifsicle(bytes, args, preArgs) -> Promise<Uint8Array>
     *   runPalette(bytes, maxColors) -> Promise<Uint8Array>   (optional)
     *   decodeFrames(bytes, count, width, height) -> Promise<Array<{data,width,height}>>
     *       MUST return frames at exactly (width, height) — a downscaled
     *       candidate has to be compared against the reference at one common
     *       size, or the `scale` family cannot be scored at all.
     *   quality: the GifQuality module
     *   isCancelled() -> bool
     *   onProgress(fraction, note)
     */
    function create(io, info, options) {
        options = options || {};
        // A user constraint, not a search parameter: when the caller says the
        // frame size must not change, `scale` (and the scale half of `combo`)
        // are not candidates at all. The optimiser does not get to trade away a
        // property the user can see and may depend on.
        var allowResize = options.allowResize !== false;
        var ctx = {
            opt: options.opt || 2,
            width: info.width,
            height: info.height,
            frames: info.frames || 1
        };
        // one common size for every comparison, so `scale` candidates score
        var metricW = Math.max(16, Math.min(METRIC_WIDTH, ctx.width));
        var metricH = Math.max(16, Math.round(ctx.height * metricW / ctx.width));
        var state = {
            proxy: null,
            refFrames: null,
            probes: [],          // every FULL-file probe, so nothing is measured twice
            proxyProbes: {}      // family -> [{t, ratio, de}]
        };

        function cancelled() { return io.isCancelled && io.isCancelled(); }
        function note(p, s) { if (io.onProgress) io.onProgress(p, s); }

        async function runCandidate(bytes, spec) {
            if (spec.palette != null) {
                var out = await io.runPalette(bytes, spec.palette);
                return spec.post ? io.runGifsicle(out, spec.post) : out;
            }
            return io.runGifsicle(bytes, spec.gifsicle);
        }

        /** Build the quality proxy + decode its reference frames. */
        async function prepare(sourceBytes) {
            if (state.proxy) return state.proxy;
            var keep = Math.max(1, Math.floor(ctx.frames / PROXY_FRAMES));
            var del = [];
            if (ctx.frames > PROXY_FRAMES) {
                for (var i = 0; i < ctx.frames; i++) if (i % keep !== 0) del.push('#' + i);
            }
            var proxyBytes = del.length
                ? await io.runGifsicle(sourceBytes, ['-O1', '--delete'].concat(del))
                : sourceBytes;
            state.proxy = proxyBytes;
            state.refFrames = await io.decodeFrames(proxyBytes, METRIC_FRAMES, metricW, metricH);
            return proxyBytes;
        }

        /**
         * Perceptual cost of a candidate, measured on the proxy: plain
         * S-CIELAB ΔE, nothing else.
         *
         * THIS USED TO CARRY A "DETAIL LOSS" TERM AND THE TERM WAS WRONG.
         * The reasoning behind it was sound — S-CIELAB blurs both pictures
         * through the eye's CSF, which is what makes it fair to dithering and
         * blind to blur, so a downscale can score better than it looks — and the
         * weight (12) was "calibrated" on the four cases where ΔE and VMAF
         * disagreed. Fitting to disagreement is fitting to noise.
         *
         * Measured properly, on 12 REAL GIFs (2.7-18 MB, video / screencast /
         * map / flag / line art / render), 221 size-comparable pairs where
         * libvmaf and S-CIELAB agree which candidate is better:
         *
         *     w = 0   97.3 % agreement      ← plain ΔE
         *     w = 4   95.0 %
         *     w = 12  92.3 %                ← what had shipped
         *     w = 30  86.4 %
         *
         * monotonically worse with w, and leave-one-file-out fitting picks w = 0
         * for 11 of the 12 files (mean held-out agreement 94.9 %). The term did
         * not correct a bias; it added one.
         *
         * The real question it was trying to answer — "may the tool trade frame
         * size for picture?" — is not a metric question at all. It is the user's
         * call, and it is answered where it belongs: the keep-frame-size
         * constraint and the resize disclosure in the result. Metrics measure;
         * the product decides.
         *
         * (`detailLoss` is still computed by the metric — it is nearly free and
         * describes the output honestly — it just does not get to move the
         * ranking.)
         */
        async function scoreProxy(outBytes) {
            var f = await io.decodeFrames(outBytes, METRIC_FRAMES, metricW, metricH);
            var n = Math.min(f.length, state.refFrames.length), de = 0;
            if (!n) return null;
            for (var i = 0; i < n; i++) de += io.quality.compare(state.refFrames[i], f[i]).mean;
            return de / n;
        }

        /**
         * Sweep one family on the proxy: 3 strengths -> (proxyRatio, ΔE).
         * Cheap: a proxy encode is 6-16× lighter than a full-file one.
         */
        async function sweepFamily(name) {
            if (state.proxyProbes[name]) return state.proxyProbes[name];
            var fam = FAMILIES[name];
            if (fam.needsFfmpeg && !io.runPalette) return null;
            var proxySize = state.proxy.length, points = [];
            for (var k = 0; k < 3; k++) {
                if (cancelled()) return null;
                var t = [0.25, 0.55, 0.90][k];
                var spec = fam.arg(t, ctx);
                var out, de;
                try {
                    out = await runCandidate(state.proxy, spec);
                    de = await scoreProxy(out);
                } catch (e) { continue; }
                if (de == null) continue;
                points.push({ t: t, ratio: out.length / proxySize, de: de, label: spec.label });
            }
            state.proxyProbes[name] = points.length ? points : null;
            return state.proxyProbes[name];
        }

        /**
         * Inverse of the proxy size curve: which strength `t` lands a family on
         * a given proxy ratio. Used to START the full-file bisect near the
         * answer instead of at t=0.5, and to tell whether a family can reach
         * the target at all — full-file probes are the expensive resource, and
         * spending three of them discovering that `--lossy` bottoms out above
         * the target is exactly what the old ladder used to do.
         */
        function tForRatio(points, ratio) {
            if (!points || !points.length) return null;
            var s = points.slice().sort(function (a, b) { return b.ratio - a.ratio; });
            if (ratio >= s[0].ratio) return s[0].t;
            var last = s[s.length - 1];
            if (ratio <= last.ratio) return null;   // out of this family's reach
            for (var i = 0; i < s.length - 1; i++) {
                var a = s[i], b = s[i + 1];
                if (ratio <= a.ratio && ratio >= b.ratio) {
                    var f = (Math.log(ratio) - Math.log(a.ratio)) / (Math.log(b.ratio) - Math.log(a.ratio) || 1);
                    return a.t + f * (b.t - a.t);
                }
            }
            return null;
        }

        /** Proxy ratio a family reaches at strength `t` (log-linear between points). */
        function proxyRatioAt(points, t) {
            if (!points || !points.length) return 0;
            var s = points.slice().sort(function (a, b) { return a.t - b.t; });
            if (t <= s[0].t) return s[0].ratio;
            var last = s[s.length - 1];
            if (t >= last.t) return last.ratio;
            for (var i = 0; i < s.length - 1; i++) {
                if (t >= s[i].t && t <= s[i + 1].t) {
                    var f = (t - s[i].t) / ((s[i + 1].t - s[i].t) || 1);
                    return Math.exp(Math.log(s[i].ratio) + f * (Math.log(s[i + 1].ratio) - Math.log(s[i].ratio)));
                }
            }
            return last.ratio;
        }

        /** Interpolate a family's ΔE at a proxy ratio (log-linear in ratio). */
        function deAt(points, ratio) {
            if (!points || !points.length) return Infinity;
            var sorted = points.slice().sort(function (a, b) { return b.ratio - a.ratio; });
            if (ratio >= sorted[0].ratio) return sorted[0].de;
            var last = sorted[sorted.length - 1];
            if (ratio <= last.ratio) return last.de;
            for (var i = 0; i < sorted.length - 1; i++) {
                var a = sorted[i], b = sorted[i + 1];
                if (ratio <= a.ratio && ratio >= b.ratio) {
                    var f = (Math.log(ratio) - Math.log(a.ratio)) / (Math.log(b.ratio) - Math.log(a.ratio));
                    return a.de + f * (b.de - a.de);
                }
            }
            return last.de;
        }

        /**
         * Rank the families at the size the user asked for.
         * `proxyRatio` is the target expressed on the proxy's own scale.
         */
        async function rank(proxyRatio, names) {
            // `combo` competes like everything else. It was demoted to a
            // last-resort branch on the strength of ONE synthetic fixture; on a
            // corpus of 12 real GIFs it is the option VMAF calls optimal more
            // often than any single knob (9 of 25 file/target cells), so keeping
            // it out of the ranking was throwing away the usual answer.
            names = names || (allowResize ? ['lossy', 'colors', 'scale', 'combo'] : ['lossy', 'colors']);
            var scored = [];
            for (var i = 0; i < names.length; i++) {
                var pts = await sweepFamily(names[i]);
                if (pts) scored.push({ name: names[i], de: deAt(pts, proxyRatio), points: pts });
            }
            scored.sort(function (a, b) { return a.de - b.de; });
            // Demand-driven: only pay for the 30 MB FFmpeg palette lane when the
            // cheap engines are already heading for visible damage. On easy
            // content (flat art, gentle targets) it never loads.
            //
            // The damage that matters is the best REACHABLE one. Measured on a
            // real 3 MB clip: `lossy` ranked first with ΔE 2.29 — under the
            // threshold, so the lane never loaded — while `lossy` bottomed out
            // at 0.55 of the original against a target of 0.34. The search then
            // chose between two options that were both worse than the palette
            // result nobody had asked for. A family that cannot deliver the size
            // has no business vetoing a better engine.
            var reach = [];
            for (var q = 0; q < scored.length; q++) {
                if (tForRatio(scored[q].points, proxyRatio) != null) reach.push(scored[q]);
            }
            var bestReachableDe = reach.length ? reach[0].de : Infinity;
            if (io.runPalette && bestReachableDe > VISIBLE_DAMAGE) {
                var p = await sweepFamily('palette');
                if (p) {
                    scored.push({ name: 'palette', de: deAt(p, proxyRatio), points: p });
                    scored.sort(function (a, b) { return a.de - b.de; });
                }
            }
            return scored;
        }

        /**
         * Bisect one family's strength on the FULL file for the largest output
         * that still fits `targetBytes` — largest fitting = most of the user's
         * budget used = best quality within the constraint (the Anna #465 rule,
         * kept).
         */
        // A family that turns out not to fit must cost a couple of probes, not
        // the whole budget: measured on dense foliage at 25 %, two dead-end
        // families ate six of eight probes and the family that COULD fit was
        // left with three — it landed at 69 % of the requested size, and unused
        // budget is lost quality just as surely as over-compression is.
        var SOFT_CAP_PER_FAMILY = 2;

        async function bisectFull(sourceBytes, family, targetBytes, maxProbes, onProbe, startT) {
            var fam = FAMILIES[family];
            var best = null, bestSpec = null, probes = 0, testedFloor = false;
            var over = null, under = null;   // {t,size} bracketing the target
            var first = (startT == null) ? 0.5 : clamp(startT, 0.05, 1);
            var pts = state.proxyProbes[family];
            var famSkew = null;              // this family's own proxy→file size factor

            function nextT() {
                if (!probes) return first;
                // One real probe is enough to pin THIS family's proxy→file size
                // factor, and within a family that factor barely moves: measured
                // across 12 real GIFs × 4 families, predicting a family's other
                // strengths from a single anchor lands within 1.0 % (median,
                // 7.1 % at p90). So after the first probe, aim straight at the
                // target instead of bracketing toward it — bracketing costs a
                // whole extra encode of a file that can be 20 MB.
                //
                // The factor is per FAMILY on purpose: one fit for all candidate
                // types is 4.1 % off where a fit per family is 0.4 % (same
                // corpus). That is the measurement that says these families are
                // the structure of the problem and not bookkeeping.
                if (famSkew && pts) {
                    var wantProxy = (targetBytes / sourceBytes.length) / famSkew;
                    var t2 = tForRatio(pts, wantProxy);
                    if (t2 != null) {
                        // never step back past a known-too-big probe, or past a
                        // known fit that we are trying to improve on
                        if (over && t2 <= over.t) t2 = Math.min(1, over.t + 0.03);
                        if (under && t2 >= under.t) t2 = under.t - 0.02;
                        if (t2 > 0 && t2 < 1 && (!over || t2 > over.t) && (!under || t2 < under.t)) return t2;
                    }
                }
                // A family has a floor: `lossy` bottoms out near 0.57 of the
                // original on photographic content. If the first probe
                // overshoots, test full strength NEXT rather than crawling
                // toward it — an unreachable family must hand over to the next
                // candidate instead of burning the whole probe budget.
                if (!under && !testedFloor && probes >= 1) return 1;
                // Everything probed so far FITS: the budget is being wasted, so
                // walk back toward gentler settings rather than sitting on an
                // over-compressed result (the Anna #465 failure mode).
                if (!over) return under ? Math.max(0.02, under.t * 0.55) : 0.5;
                if (!under) return Math.min(1, over.t + 0.35);
                // Log-linear interpolation between the bracketing probes. Some
                // families have a size CLIFF (gifsicle --colors goes 70→8
                // colours across a fifth of the range), and plain midpoint
                // bisection spends every probe it has walking across it.
                var f = (Math.log(over.size) - Math.log(targetBytes)) /
                        (Math.log(over.size) - Math.log(under.size) || 1);
                var t = over.t + (under.t - over.t) * f;
                return clamp(t + (under.t - over.t) * 0.05, Math.min(over.t + 0.01, under.t), under.t);
            }

            // Probe budget with an escape hatch: never walk away holding a
            // result that used less than 85 % of the size the user asked for.
            // Measured on a 9.4 MB foliage GIF at 25 %, stopping on the budget
            // handed back 69 % of the target — and the third of the budget left
            // on the table is exactly the quality the user was trying to buy.
            // The extra probes are spent only in that case.
            var hardMax = maxProbes + 2;
            while (probes < maxProbes || (best && best.length < targetBytes * 0.85 && probes < hardMax)) {
                if (cancelled()) return null;
                var t = nextT();
                if (t >= 1) testedFloor = true;
                var spec = fam.arg(t, ctx);
                var out;
                try { out = await runCandidate(sourceBytes, spec); }
                catch (e) { break; }
                probes++;
                if (onProbe) onProbe();
                state.probes.push({ family: family, t: t, size: out.length, label: spec.label });
                // calibrate this family from its first real probe
                if (famSkew == null && pts) {
                    var pr = proxyRatioAt(pts, t);
                    if (pr > 0) famSkew = clamp((out.length / sourceBytes.length) / pr, 0.5, 4);
                }
                if (out.length <= targetBytes) {
                    if (!best || out.length > best.length) { best = out; bestSpec = spec; }
                    if (!under || t < under.t) under = { t: t, size: out.length };
                } else {
                    if (!over || t > over.t) over = { t: t, size: out.length };
                    if (t >= 1) break;   // even at full strength it overshoots
                }
                if (!best && probes >= SOFT_CAP_PER_FAMILY) break;   // dead end — hand over
                if (over && under && (under.t - over.t) <= 0.02) break;
                // Stop once the budget is essentially used up. Unused budget is
                // lost quality (the Anna #465 rule): a result at 46 % of the
                // requested size is not "safely under", it is a picture the user
                // did not ask to have destroyed.
                if (best && best.length >= targetBytes * 0.95) break;
            }
            return best ? { bytes: best, spec: bestSpec, family: family, probes: probes } : { unreachable: true, probes: probes };
        }

        /**
         * Main entry: best-looking result that fits `targetBytes`.
         * Returns null when nothing fits — the caller keeps its existing
         * "target unreachable" handling.
         */
        async function forTarget(sourceBytes, targetBytes, budget) {
            budget = budget || {};
            // Full-file encodes are the expensive resource. The search this
            // replaced spent 8-14 of them; this budget covers the lossless
            // probe, one calibration probe and the bisect, and is deliberately
            // set where measurement says the target is reliably hit.
            var maxFull = budget.maxFullProbes || 8;

            // Phase 0, kept from the old search and for the same reason (Anna
            // #465, "нужно до 1 мегабайта, делает либо 300 либо 400 кб"): if a
            // purely LOSSLESS pass already fits, that is the best-quality answer
            // that exists and no search should be run at all.
            try {
                var lossless = await runCandidate(sourceBytes, { gifsicle: ['-O' + ctx.opt] });
                state.probes.push({ family: 'lossless', t: 0, size: lossless.length, label: 'lossless' });
                if (lossless.length <= targetBytes) {
                    return { bytes: lossless, spec: { label: 'lossless' }, family: 'lossless', quality: 0, ranked: [] };
                }
            } catch (e) { /* fall through to the search */ }

            await prepare(sourceBytes);
            if (cancelled()) return null;
            note(0.15, 'analyze');

            // The proxy's ratios live on a different scale than the file's
            // (measured skew ~1.3-1.5×), so express the target on the proxy's
            // scale before asking which family wins there. The skew only steers
            // the family choice; every size below is measured for real.
            var wanted = targetBytes / sourceBytes.length;

            // The proxy compresses harder than the file it came from (fewer
            // frames = less inter-frame redundancy), so its ratios live on a
            // different scale. Measure that skew instead of assuming it: sweep
            // one family on the proxy, then run its middle strength ONCE on the
            // real file. Guessing the skew put the ranking at the wrong point of
            // a steep quality curve and the palette lane never got tried on the
            // content that needs it most.
            var lossyPts = await sweepFamily('lossy');
            var skew = 1.4, seed = null, seedSpec = null;
            if (lossyPts && lossyPts.length) {
                var calPt = lossyPts[Math.min(1, lossyPts.length - 1)];
                var calSpec = FAMILIES.lossy.arg(calPt.t, ctx);
                try {
                    var calOut = await runCandidate(sourceBytes, calSpec);
                    state.probes.push({ family: 'lossy', t: calPt.t, size: calOut.length, label: calSpec.label });
                    if (calPt.ratio > 0) skew = clamp((calOut.length / sourceBytes.length) / calPt.ratio, 1.0, 2.5);
                    if (calOut.length <= targetBytes) { seed = calOut; seedSpec = calSpec; }
                } catch (e) { /* keep the default skew */ }
            }
            if (cancelled()) return null;
            var proxyRatio = clamp(wanted / skew, 0.05, 0.98);
            var ranked = await rank(proxyRatio);
            if (cancelled() || !ranked.length) return null;
            note(0.45, ranked[0].name);

            // Walk the ranked families best-first until one both REACHES the
            // target and fits it, under a shared probe budget. Ranking decides
            // preference; reachability is only ever settled by a real encode.
            // Strictly best-quality first. The proxy's own ratios are on a
            // different scale from the file's, so its idea of "can this family
            // reach the target" is only good enough to pick a STARTING point —
            // reordering on it demoted the family that measured best and cost
            // real quality. A family the proxy thinks is out of reach starts at
            // full strength, so proving it costs one probe instead of two.
            var spent = 0, result = null;
            for (var i = 0; i < ranked.length && spent < maxFull; i++) {
                var startT = tForRatio(ranked[i].points, proxyRatio);
                var r = await bisectFull(sourceBytes, ranked[i].name, targetBytes,
                    Math.max(2, maxFull - spent), function () {
                        spent++;
                        note(0.45 + 0.5 * Math.min(1, spent / maxFull), ranked[i].name);
                    }, startT == null ? 1 : startT);
                if (r && r.bytes) { result = r; break; }
                if (cancelled()) return null;
            }
            // (No last-resort branch any more: `combo` is in the ranked list, so
            // stacking the knobs is reached the same way as everything else —
            // by measuring better at the size that was asked for.)
            // The calibration probe is a real candidate too — keep it if it used
            // more of the budget than anything the bisect found.
            if (seed && (!result || seed.length > result.bytes.length)) {
                result = { bytes: seed, spec: seedSpec, family: 'lossy' };
            }
            if (!result) return null;
            try { result.quality = await scoreProxy(await runCandidate(state.proxy, result.spec)); }
            catch (e) { result.quality = null; }
            result.ranked = ranked.map(function (r) { return { family: r.name, de: r.de }; });
            return result;
        }

        /**
         * "Quick" = compress as far as the picture allows, not a size bucket.
         * Picks the strongest setting whose measured damage stays under
         * `maxDeltaE`, using the family that is gentlest for this content.
         */
        async function forQuality(sourceBytes, maxDeltaE) {
            await prepare(sourceBytes);
            if (cancelled()) return null;
            // NO `scale` here. Downscaling measures well — on photographic
            // content both S-CIELAB and VMAF prefer it at aggressive sizes — but
            // resolution is a property the user can see and did not ask to
            // change. Trading it away belongs to Target mode, where a byte
            // budget was explicitly bought; "Quick" must mean smaller file,
            // same picture. (Measured: unrestricted Quick took 560×315 down to
            // 316×178 on its own.)
            var ranked = await rank(0.45, ['lossy', 'colors']);
            if (!ranked.length) return null;
            var winner = ranked[0];
            // walk the family's own curve for the strongest t under the budget
            var pts = winner.points.slice().sort(function (a, b) { return a.t - b.t; });
            var t = pts[0].t;
            for (var i = 0; i < pts.length; i++) if (pts[i].de <= maxDeltaE) t = pts[i].t;
            // one refinement step between the last passing point and the first failing one
            var fail = null;
            for (var j = 0; j < pts.length; j++) if (pts[j].de > maxDeltaE) { fail = pts[j].t; break; }
            if (fail != null && fail > t) {
                var mid = (t + fail) / 2;
                var spec0 = FAMILIES[winner.name].arg(mid, ctx);
                try {
                    var de = await scoreProxy(await runCandidate(state.proxy, spec0));
                    if (de != null && de <= maxDeltaE) t = mid;
                } catch (e) { /* keep the safe t */ }
            }
            var spec = FAMILIES[winner.name].arg(t, ctx);
            var out = await runCandidate(sourceBytes, spec);
            var q = null;
            try { q = await scoreProxy(await runCandidate(state.proxy, spec)); } catch (e) { /* keep null */ }
            return {
                bytes: out, spec: spec, family: winner.name, quality: q,
                ranked: ranked.map(function (r) { return { family: r.name, de: r.de }; })
            };
        }

        return {
            prepare: prepare,
            rank: rank,
            forTarget: forTarget,
            forQuality: forQuality,
            scoreProxy: scoreProxy,
            state: state
        };
    }

    return { create: create, FAMILIES: FAMILIES, VISIBLE_DAMAGE: VISIBLE_DAMAGE };
}));
